import type { ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { BareLayout } from './shell/BareLayout'
import { ChromeLayout } from './shell/ChromeLayout'
import { ALL_NAV_ITEMS, SETTINGS_NAV } from './shell/nav'
import { RequireAuth, RedirectWhenSignedIn, MFA_PATH } from './auth/RequireAuth'
import { AcceptInvitation } from './routes/auth/AcceptInvitation'
import { AccountDetail } from './routes/accounts/AccountDetail'
import { ACCOUNTS_PATH } from './routes/accounts/accountPaths'
import { Accounts } from './routes/accounts/Accounts'
import { Connect } from './routes/accounts/Connect'
import { Connections } from './routes/accounts/Connections'
import { FlinksCallback } from './routes/accounts/FlinksCallback'
import { PlaidOAuthReturn } from './routes/accounts/PlaidOAuthReturn'
import { RelinkMatch } from './routes/accounts/RelinkMatch'
import { RELINK_MATCH_ROUTE } from './routes/accounts/relinkRoutes'
import { Categories } from './routes/categories/Categories'
import { CATEGORIES_PATH } from './routes/categories/categoryPaths'
import { CategoryDetail } from './routes/categories/CategoryDetail'
import { ForgotPassword } from './routes/auth/ForgotPassword'
import { Help } from './routes/support/Help'
import { Legal } from './routes/support/Legal'
import { Household } from './routes/household/Household'
import { MfaChallenge } from './routes/auth/MfaChallenge'
import { Cashflow } from './routes/cashflow/Cashflow'
import { Reports } from './routes/reports/Reports'
import { CashflowDetail } from './routes/cashflow/CashflowDetail'
import { CASHFLOW_DETAIL_PATH } from './routes/cashflow/cashflowScope'
import { Merchants } from './routes/merchants/Merchants'
import { MERCHANTS_PATH } from './routes/merchants/merchantPaths'
import { Tags } from './routes/tags/Tags'
import { TAGS_PATH } from './routes/tags/tagPaths'
import { Transactions } from './routes/transactions/Transactions'
import { Review } from './routes/review/Review'
import { TransactionDetail } from './routes/transactions/TransactionDetail'
import { RulePage } from './routes/rules/RulePage'
import { Rules } from './routes/rules/Rules'
import { MerchantTransactions } from './routes/merchants/MerchantTransactions'
import { NotFound } from './routes/errors/NotFound'
import { Offline } from './routes/errors/Offline'
import { Probe } from './routes/probe/Probe'
import { AllRecurring } from './routes/recurring/AllRecurring'
import { Recurring, RecurringCalendarRedirect } from './routes/recurring/Recurring'
import { RecurringDetail } from './routes/recurring/RecurringDetail'
import { Suggestions } from './routes/recurring/Suggestions'
import { RecurringEdit, RecurringNew } from './routes/recurring/RecurringForm'
import {
  RECURRING_ALL_PATH,
  RECURRING_CALENDAR_PATH,
  RECURRING_DETAIL_PATH,
  RECURRING_EDIT_PATH,
  RECURRING_NEW_PATH,
  RECURRING_PATH,
  RECURRING_SUGGESTIONS_PATH,
} from './routes/recurring/recurringPaths'
import { ResetPassword } from './routes/auth/ResetPassword'
import { SessionExpired } from './routes/auth/SessionExpired'
import { SignIn } from './routes/auth/SignIn'
import { SignUp } from './routes/auth/SignUp'
import { VerifyDevice } from './routes/auth/VerifyDevice'
import { VerifyEmail } from './routes/auth/VerifyEmail'
import { Devices } from './routes/settings/Devices'
import { Billing } from './routes/settings/Billing'
import { Developer } from './routes/settings/Developer'
import { Preferences } from './routes/settings/Preferences'
import { Profile } from './routes/settings/Profile'
import { Security } from './routes/settings/Security'
import { SettingsLayout } from './routes/settings/SettingsLayout'
import { Welcome } from './routes/settings/Welcome'
import { StatesReference } from './routes/states/StatesReference'
import { Budget } from './routes/budget/Budget'
import { BUDGET_DETAIL_PATH, BUDGET_PATH } from './routes/budget/budgetPaths'
import { BudgetDetail } from './routes/budget/BudgetDetail'
import { Overview } from './routes/overview/Overview'
import { OVERVIEW_PATH } from './routes/overview/overviewPaths'

/**
 * The route table — Task 18.
 *
 * **Every nav destination gets a route.** The plan routes two of the sidebar's
 * 25 links and sends the rest to a catch-all redirect, so 23 links would land
 * silently on Overview: a navigation that lies about where it goes, and a shell
 * whose active-route marker would point at the wrong item. Generating a
 * placeholder per nav item from `NAV_GROUPS` keeps the two in step by
 * construction — a link added to the nav cannot ship without a destination.
 *
 * **`*` states that it is lost rather than redirecting.** `ChromeLayout`
 * already computes "Not found" for a path it cannot name, and the plan's
 * `<Navigate to="/overview">` makes that unreachable while turning every typo
 * into a silent landing on the wrong page. The 404 keeps the chrome, so the
 * user can navigate away instead of being stranded.
 */

/**
 * The screens that are actually built, keyed by their nav path.
 *
 * An inclusion map, not an exclusion filter. The first version wrote
 * `.filter((i) => i.path !== '/states')`, which by M14 is a 25-entry list
 * maintained by hand — the same enumeration failure the generation exists to
 * avoid. Adding a screen is one line here and deletes nothing.
 */
const SCREENS: Record<string, ReactNode> = {
  '/states': <StatesReference />,
  '/help': <Help />,
  '/legal': <Legal />,
  '/settings/profile': <Profile />,
  '/settings/preferences': <Preferences />,
  '/settings/billing': <Billing />,
  '/settings/security': <Security />,
  '/settings/devices': <Devices />,
  [ACCOUNTS_PATH]: <Accounts />,
  '/connections': <Connections />,
  [CATEGORIES_PATH]: <Categories />,
  '/household': <Household />,
  [MERCHANTS_PATH]: <Merchants />,
  [TAGS_PATH]: <Tags />,
  '/transactions': <Transactions />,
  '/cashflow': <Cashflow />,
  '/reports': <Reports />,
  [OVERVIEW_PATH]: <Overview />,
  [BUDGET_PATH]: <Budget />,
  '/review': <Review />,
  '/rules': <Rules tab="rules" />,
  [RECURRING_PATH]: <Recurring />,
  /**
   * #449. Spread from an inline MODE check, not written as a plain entry: an
   * eager map entry keeps its element — and so the page, its copy and the
   * backend host list — in every bundle whether or not a route renders it.
   * Folded to `...{}` in production, which is what lets the bundler drop the
   * import above. The nav entry that generates the route is gated the same way
   * in `shell/nav.ts`.
   */
  ...(import.meta.env.MODE !== 'production' ? { '/settings/developer': <Developer /> } : {}),
}

/**
 * A route that exists but has no screen yet.
 *
 * **Deliberately not an `<h1>`.** `ChromeLayout` renders the page name as the
 * document heading — #32's decision, because a screen-reader user navigates by
 * heading before anything else and every route needed exactly one. A route that
 * also titles itself gives every page two `<h1>`s with the same text, which is
 * what the first version of this file did and what its own 404 test caught.
 * Route content starts at `<h2>`.
 */
function Placeholder({ name }: { name: string }) {
  return <p className="route-note">{name} is not built yet.</p>
}

/**
 * A path that matches nothing — now `routes/errors/NotFound`, not a `<p>` here.
 *
 * The sentence this replaced was already making the right argument: its own
 * copy rather than `Placeholder`'s, because "Not found is not built yet" says a
 * page that does not exist is merely pending. What it could not do was be the
 * screen the design actually draws — a badge, a heading, the two recovery
 * routes, and the path that missed. It stays inside `ChromeLayout` for the same
 * reason it always did: a 404 with no navigation is a dead end.
 */

/**
 * A pre-login placeholder, which DOES own its heading.
 *
 * `BareLayout` has no `TopBar`, so nothing else renders an `<h1>` on these
 * routes — and the first version of this file left `/signin` with no document
 * heading at all, which a browser check caught and the suite did not. The
 * chrome and bare cases genuinely differ: under chrome the shell owns the
 * heading, here the route does.
 */
function BarePlaceholder({ name }: { name: string }) {
  return <h1 className="page-title">{name}</h1>
}

/**
 * Pre-login surfaces render bare — no sidebar, no top bar.
 *
 * `/forgot-password`, not `/reset-password`. The route table in the design spec
 * says the former and this file said the latter; the plan's self-review found
 * the disagreement and settled it in favour of the spec. Renamed here rather
 * than in Task 9 because the sign-in screen links to it today, and a link to a
 * path with no route lands on the 404 — a dead end at the exact moment a user
 * has already told us they cannot get in.
 */
/**
 * Where a user lands when their session ends under them. In `BARE`, because a
 * signed-in user genuinely does not belong on it.
 *
 * Declared ABOVE `BARE` rather than beside its two siblings further down, and
 * that placement is load-bearing: `BARE` reads it at module evaluation, `const`
 * is not hoisted, and the tidier grouping put it in the temporal dead zone —
 * every route in the app crashed on import. Caught by typecheck, but it would
 * have been a blank page.
 */
const SESSION_EXPIRED_PATH = '/session-expired'

/**
 * **Membership here is not "has no chrome" — it is "a signed-in user must be
 * BOUNCED off it".** This generation is wrapped in `RedirectWhenSignedIn`, and
 * that is the whole test for belonging.
 *
 * Stated this loudly because the group has now caught out four routes. The
 * three below it in the file — `/verify/:token`, `/welcome`, and the two added
 * with the reset and verify screens — are all bare, all chromeless, and all
 * correctly OUTSIDE this array, for one of two reasons:
 *
 *   - the screen is FOR a signed-in user (`/welcome`, `/verify-email`), so the
 *     guard would bounce away the only person it serves; or
 *   - BOTH states legitimately reach it (`/verify/:token`, `/reset`), because
 *     the link arrives by email and the browser that opens it may well already
 *     have a session.
 *
 * `/reset` is the sharpest case and the easiest to get wrong: "forgot my
 * password" is most often typed on a machine where you are still signed in
 * somewhere else, so putting it here would send the user to Overview and leave
 * them permanently unable to spend the link. Two independent readers reached
 * that conclusion separately while this batch was being built, which is the
 * best argument for writing it down.
 */
const BARE = [
  { path: '/signin', name: 'Sign in' },
  { path: '/signup', name: 'Sign up' },
  { path: '/forgot-password', name: 'Forgot password' },
  // Pre-login by definition: a second factor is asked for BEFORE there is a
  // session, so `RequireAuth` would bounce this route to /signin and the
  // challenge would have nowhere to be answered.
  { path: MFA_PATH, name: 'Two-step verification' },
  // Signed-out by definition too: it is the screen a user lands on BECAUSE
  // their session ended, so a live session means they do not belong here and
  // the bounce to Overview is the correct answer rather than a hazard.
  { path: SESSION_EXPIRED_PATH, name: 'Session expired' },
]

/** The screens that exist on a bare route. Same inclusion map as `SCREENS`. */
const BARE_SCREENS: Record<string, ReactNode> = {
  '/signin': <SignIn />,
  '/signup': <SignUp />,
  '/forgot-password': <ForgotPassword />,
  [MFA_PATH]: <MfaChallenge />,
  [SESSION_EXPIRED_PATH]: <SessionExpired />,
}

/**
 * The device-approval link, which is bare but is NOT one of the BARE routes.
 *
 * Public by design: the token is emailed to the account holder on a new-country
 * sign-in and it *is* the credential, so the page has to work with no session —
 * the email often opens on a phone that has never used the web app.
 *
 * Held out of `BARE` deliberately, and the reason is Task 15 rather than today:
 * that task wraps the BARE generation in `RedirectWhenSignedIn`, which would
 * bounce a signed-IN user off this page to Overview. Both states have to reach
 * it — the person approving may well be signed in on this browser already —
 * so it is a sibling of that group rather than a member of it.
 */
const VERIFY_DEVICE_PATH = '/verify/:token'

/**
 * Task 9's copied invite link, and the reason it means anything — see
 * `AcceptInvitation`'s own docblock for the full argument. Held out of `BARE`
 * for the identical reason `VERIFY_DEVICE_PATH` is: the browser that opens an
 * invite link is very often one where the recipient is already signed in, and
 * `RedirectWhenSignedIn` would bounce them to Overview before the token could
 * be spent. Both states have to reach it, so it is a sibling of that group
 * rather than a member.
 *
 * `GET /invitations/{token}/open` is a different path and not this one — that
 * is the mobile-only redirect issue 305 describes; this route posts straight
 * to `accept`.
 */
const INVITE_PATH = '/invite/:token'

/**
 * The reset link's landing page — bare, and held out of `BARE` for exactly the
 * reason above it.
 *
 * Firebase mails `?mode=resetPassword&oobCode=…`, and the browser that opens
 * that mail is very often one where the user is still signed in: "I forgot my
 * password" is a thing people discover on a second device, not a fresh one.
 * Inside `BARE` this route would redirect them to Overview and the link could
 * never be spent — the forgot-password flow would end one step from the finish.
 *
 * `/reset`, not `/reset-password`: the sibling `/forgot-password` is the
 * REQUEST and this is the SPEND, and naming them near-identically is how a
 * future reader wires a link to the wrong one.
 */
const RESET_PATH = '/reset'

/**
 * Verification is FOR a signed-in user, which is what keeps it out of `BARE`.
 *
 * Firebase creates the account first and verifies the address afterwards, so
 * everyone who needs this screen already has a session — `RedirectWhenSignedIn`
 * would bounce every one of them. Same reasoning as `WELCOME_PATH` below.
 */
const VERIFY_EMAIL_PATH = '/verify-email'

/**
 * Offline — still a route, and STILL deliberately not an automatic takeover.
 * #274 decided the open question this docblock used to leave filed rather than
 * answered: a `<OfflineBanner>`, mounted above the router in `main.tsx`, is
 * `useOnline()`'s actual consumer now, and this route is not it.
 *
 * The reasoning against a takeover did not change and is worth keeping rather
 * than re-deriving: this screen's own copy says *"what's already loaded in
 * this tab is still here to read"*, and swapping the whole app for it the
 * moment `navigator.onLine` goes false is precisely what would stop that being
 * true — a screen cannot promise the page behind it survives and then replace
 * that page. TanStack Query keeps serving its in-memory cache regardless of
 * connectivity, so a banner is not a lesser answer standing in for a takeover;
 * it is the answer this screen's own promise requires.
 *
 * What #274 actually decided is that "banner" won over "takeover with
 * different copy" and over "leave it a route nothing links to" — the three
 * options this docblock used to leave open. This route stays, reachable and
 * reviewable exactly as before: a user who lands on `/offline` directly (a
 * bookmark, a stale tab) still sees the full screen, and it is a second,
 * independent way to see the same fact the banner reports everywhere else —
 * not a component the banner deletes or depends on.
 *
 * Out of `BARE` for the usual reason: losing your connection is not a fact
 * about being signed in, so both states have to reach it.
 */
const OFFLINE_PATH = '/offline'

/**
 * The dev-only screen probe — see `routes/probe/Probe.tsx`.
 *
 * **Declared unconditionally, ROUTED conditionally.** `App.test.tsx` reads this
 * file's source for `path={CONSTANT}` and requires every one to appear in
 * `ROUTE_PATHS`; a constant hidden behind a build flag would either fail that
 * guard or quietly teach it to skip route declarations, and the guard is worth
 * more than the tidiness. The `<Route>` below is what `import.meta.env.DEV`
 * gates, and Vite folds that to a literal `false` in a production build, so the
 * element and everything it imports are dropped.
 *
 * One route with a `?screen=` query rather than `/__probe/:screen`: the browser
 * sweeps walk `ROUTE_PATHS` literally, and a parameterised path would have them
 * visiting the string ":screen". This way they land on the index, which is an
 * ordinary page they can measure.
 */
const PROBE_PATH = '/__probe'


/**
 * Every path this app routes — the union, derived where `AppRoutes` derives it.
 *
 * The app has more than one route source, and anything sweeping "all routes"
 * has to mean all of them. Exporting the individual sources instead just hands
 * the caller a second enumeration to keep in step, which is the failure being
 * avoided rather than a fix for it: the first version of the composition
 * tripwire iterated `NAV_GROUPS` alone and was blind to all three bare routes.
 *
 * **`/` used to be left out and now is not.** The old reasoning — "a redirect to
 * `/overview`, which is in the list, so it is covered by what it resolves to" —
 * holds for the SWEEPS and fails for the other reader. `App.test`'s "links only
 * to routes that exist" asks whether an href resolves to anything, not what it
 * resolves to, and under the old list a link to `/` was indistinguishable from
 * a link to a route that was never declared. Nothing linked to `/` until the
 * pre-login brand block did (issue 271), so the gap had never been reachable.
 * The sweeps are unaffected: they still land on `/overview` through the
 * redirect, they now just start one hop earlier.
 *
 * `*` is still not in the list — no path here renders `NotFound`, and a sweep
 * over this list would never see the catch-all subtree at all. The sentinel
 * closes that: it matches nothing, so it resolves to `*` by definition, and one
 * unmatched path is enough to sweep a catch-all.
 *
 * **Derived from the same arrays `AppRoutes` maps, not from `AppRoutes`.** A
 * `<Route path="/onboarding">` typed straight into the JSX would be invisible
 * here — which is not hypothetical, since the two one-off routes that exist
 * today are exactly that shape. `App.test.tsx` asserts every literal `path=` in
 * this file is accounted for, which is what narrows the gap from "a second
 * array" to nothing.
 */
export const CATCH_ALL_SENTINEL = '/__no-such-route__'

/**
 * First run. Declared HERE, like `VERIFY_DEVICE_PATH`, because `App.test.tsx`
 * resolves route-path constants against declarations in this file and fails on
 * ones it cannot see. `RequireAuth` takes it as a prop for that reason.
 */
export const WELCOME_PATH = '/welcome'

/**
 * The root, which is a real route and was missing from the union below.
 *
 * `<Route path="/">` has been declared since the redirect landed, but nothing
 * listed it here — so `ROUTE_PATHS` claimed to be "every path this app routes"
 * while omitting the first one anyone types. Nothing caught it because nothing
 * linked to `/` until the pre-login brand block did (issue 271), at which point
 * App.test's "links only to routes that exist" failed on a link that pointed at
 * a route that does exist.
 */
export const ROOT_PATH = '/'

/**
 * The category detail route — Task 10 (M11), and NOT a `SCREENS` entry.
 *
 * `SCREENS` is keyed by NAV path (`ALL_NAV_ITEMS`) — its own docblock says so
 * — and `/categories/:id` is not a nav destination, it is a child of one. The
 * generated `<Route path={`${i.path}/*`}>` below already matches
 * `/categories/f`, because it has to match `/categories/anything` to own the
 * subtree at all, and renders `<Categories />` for it.
 *
 * A sibling `<Route path={CATEGORY_DETAIL_PATH}>` fixes that without touching
 * the generation loop: React Router ranks a literal `:id` segment above a
 * trailing `*`, in EITHER declaration order, so this route wins the match for
 * `/categories/<anything but nothing>` and the generated splat keeps
 * `/categories` itself. `CategoryDetail` decides for itself whether the id it
 * received names a real category.
 */
export const CATEGORY_DETAIL_PATH = '/categories/:id'

/**
 * The suggestions tab — a sibling `<Route>`, NOT a `SCREENS` entry, for the
 * reason `CATEGORY_DETAIL_PATH` above gives: `SCREENS` is keyed by nav path and
 * `/rules/suggestions` is a child of one. React Router ranks the literal
 * segment above the generated `/rules/*` splat in either declaration order.
 */
export const RULE_SUGGESTIONS_PATH = '/rules/suggestions'

/**
 * One rule's page, M11 (rule view). A sibling `<Route>` for the reason `RULE_SUGGESTIONS_PATH` gives;
 * the literal `/rules/suggestions` outranks this dynamic segment in either declaration order, so a
 * rule is never fetched by the id "suggestions". `RulePage` decides whether the id names a rule.
 */
export const RULE_DETAIL_PATH = '/rules/:id'

/**
 * The account detail route — Ticket 2 (M5), same shape as
 * `CATEGORY_DETAIL_PATH` just above and for the identical reason: `/accounts`
 * is a nav destination and its generated `<Route path="/accounts/*">` below
 * already matches `/accounts/anything`, rendering `<Accounts />` for it. This
 * sibling with a literal `:id` segment wins the match instead (React Router
 * ranks it above a trailing `*` regardless of declaration order), so
 * `/accounts/<real id>` reaches `<AccountDetail />` and the generated splat
 * keeps `/accounts` itself.
 */
export const ACCOUNT_DETAIL_PATH = '/accounts/:id'

/**
 * `/transactions/:id` — M6 Task 11. The same trap `ACCOUNT_DETAIL_PATH` documents:
 * the generated `/transactions/*` below already matches this URL and renders the
 * list. This sibling route with a dynamic segment is ranked above that splat
 * regardless of declaration order, so a real id reaches the detail and the bare
 * `/transactions` keeps the list.
 */
export const TRANSACTION_DETAIL_PATH = '/transactions/:id'

/**
 * `/accounts/connect` — Ticket 3 (M5, #397). Same ranking as
 * `ACCOUNT_DETAIL_PATH` just above and for the identical reason: it is a
 * LITERAL second segment competing with `:id` at the same depth under the
 * generated `/accounts/*`. React Router ranks a static segment above a
 * dynamic one regardless of declaration order, so `/accounts/connect`
 * resolves here rather than being read as `AccountDetail` given the id
 * `"connect"`.
 */
export const ACCOUNT_CONNECT_PATH = '/accounts/connect'

/**
 * `/flinks/callback` — the ONE path `callback_paths` names today (epic #6).
 * Bare, like `VERIFY_DEVICE_PATH`: it runs in a POPUP tab `flinks.ts` opened,
 * which shares the opener's session but should not be made to wait on
 * `RequireAuth`'s own redirect plumbing for a screen whose entire job is to
 * post one message and close itself.
 */
export const FLINKS_CALLBACK_PATH = '/flinks/callback'

/**
 * The merchant transactions route — the design that arrived 2026-08-21,
 * replacing Task 11's `/merchants/:id` (removed: that screen no longer
 * exists, a row's chevron expands it in place instead — see `Merchants.tsx`).
 *
 * Same trap `CATEGORY_DETAIL_PATH` documents just above, one segment deeper:
 * the generated `<Route path={`${i.path}/*`}>` below already matches
 * `/merchants/1/transactions`, because it has to match
 * `/merchants/anything` to own the subtree at all, and renders `<Merchants
 * />` for it. This sibling with a literal `:id` PLUS a literal `transactions`
 * segment wins the match instead, the same ranking `CATEGORY_DETAIL_PATH`
 * relies on. `MerchantTransactions` decides for itself whether the id it
 * received names a real merchant.
 */
export const MERCHANT_TRANSACTIONS_PATH = '/merchants/:id/transactions'

/**
 * The cashflow drill-down — one row of `/cashflow` opened on its own (M3).
 *
 * Same trap `CATEGORY_DETAIL_PATH` documents: the generated
 * `<Route path={`${i.path}/*`}>` below already matches `/cashflow/category/x`,
 * because it has to match `/cashflow/anything` to own the subtree at all, and
 * renders the overview for it. This sibling with a dynamic `:scope` segment
 * (and an optional `:id`, which `uncategorised` has none of) wins the match
 * instead, in either declaration order, and the generated splat keeps
 * `/cashflow` itself. `CashflowDetail` decides for itself whether the scope it
 * received is a real one; an unknown word renders `NotFound`.
 *
 * `CASHFLOW_DETAIL_PATH` itself is declared in `cashflowScope.ts`, beside the
 * `detailPath` builder that writes every link to it, as `RECURRING_DETAIL_PATH`
 * is in `recurringPaths.ts`; `App.test.tsx`'s route-table guard reads it there.
 */

/**
 * `/plaid` — #414. Where Plaid's dashboard has a redirect URI registered
 * (`https://app-dev.tracke5.com/plaid/`) and where an OAuth institution's
 * full-page handoff actually returns the browser.
 *
 * No trailing slash, matching every other constant on this page — verified
 * against `react-router@8.3.0`'s own `compilePath`
 * (`node_modules/react-router/dist/development/lib/router/utils.js:510`):
 * with the default `end: true` the compiled matcher always appends `\/*$`,
 * so this still matches `/plaid/` exactly. Not assumed — read the regex
 * construction (`PlaidOAuthReturn.tsx`'s own header has the full note).
 *
 * A sibling of `ACCOUNT_CONNECT_PATH` below rather than a `SCREENS` entry —
 * it is not a nav destination, same reasoning as that route.
 *
 * Declared as its own literal here rather than imported from
 * `providers/plaidOAuth.ts` (which needs the identical string to build the
 * redirect URI it sends the backend): `App.test.tsx`'s route-table guard
 * resolves `path={CONSTANT}` against a literal `const NAME = '...'`
 * declared IN THIS FILE, so an imported constant would be invisible to it.
 * `plaidOAuth.test.ts` cross-checks the two literals stay in sync.
 */
export const PLAID_OAUTH_RETURN_PATH = '/plaid'

/**
 * M7's old calendar view — `RECURRING_CALENDAR_PATH`, declared in
 * `routes/recurring/recurringPaths.ts` beside `RECURRING_PATH`. Since #704 the
 * month grid sits on `/recurring` itself, so this path only REPLACE-redirects
 * there, keeping `?month=`. A sibling of the generated `/recurring/*` and
 * ranked above it by its literal second segment, as `RULE_SUGGESTIONS_PATH`
 * is, so "calendar" is never read as a bill id (Ruling 41).
 */

/**
 * M7 Task 9, one bill (M7-R10–R13, P4) — `RECURRING_DETAIL_PATH`, declared in
 * `recurringPaths.ts` with its `recurringDetailPath(id)` builder. A sibling of
 * the generated `/recurring/*`, as `TRANSACTION_DETAIL_PATH` is of
 * `/transactions/*`. The calendar, new, all and suggestions literals each have
 * their own Route, which outranks this `:id` whatever the declaration order
 * (Ruling 41): without them `/recurring/new` was fetched as a bill.
 */

export const ROUTE_PATHS: readonly string[] = [
  ROOT_PATH,
  ...BARE.map((r) => r.path),
  VERIFY_DEVICE_PATH,
  INVITE_PATH,
  RESET_PATH,
  VERIFY_EMAIL_PATH,
  OFFLINE_PATH,
  PROBE_PATH,
  WELCOME_PATH,
  CATEGORY_DETAIL_PATH,
  RULE_SUGGESTIONS_PATH,
  RULE_DETAIL_PATH,
  ACCOUNT_DETAIL_PATH,
  TRANSACTION_DETAIL_PATH,
  ACCOUNT_CONNECT_PATH,
  FLINKS_CALLBACK_PATH,
  MERCHANT_TRANSACTIONS_PATH,
  CASHFLOW_DETAIL_PATH,
  PLAID_OAUTH_RETURN_PATH,
  RECURRING_CALENDAR_PATH,
  RECURRING_NEW_PATH,
  RECURRING_ALL_PATH,
  RECURRING_SUGGESTIONS_PATH,
  RECURRING_DETAIL_PATH,
  RECURRING_EDIT_PATH,
  BUDGET_DETAIL_PATH,
  RELINK_MATCH_ROUTE,
  ...ALL_NAV_ITEMS.map((i) => i.path),
  CATCH_ALL_SENTINEL,
]

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<BareLayout />}>
        {/* Task 10's other guard, and the one that gets forgotten: without it a
            phone coming back from Google's redirect lands on /signin ALREADY
            signed in, with the form still in front of it and nothing
            happening. It also owns where a cleared MFA challenge goes — see
            MfaChallenge, which defers to it by rendering nothing once this
            guard can act. */}
        <Route element={<RedirectWhenSignedIn />}>
          {BARE.map((r) => (
            <Route
              key={r.path}
              path={r.path}
              element={BARE_SCREENS[r.path] ?? <BarePlaceholder name={r.name} />}
            />
          ))}
        </Route>
      </Route>

      {/* Its own `BareLayout` block, outside the generation above. See
          VERIFY_DEVICE_PATH for why it must not join that group. */}
      <Route element={<BareLayout />}>
        <Route path={VERIFY_DEVICE_PATH} element={<VerifyDevice />} />
        {/* A sibling of the BARE group, not a member — see INVITE_PATH for
            why a link from an email must reach both signed-in and
            signed-out users. */}
        <Route path={INVITE_PATH} element={<AcceptInvitation />} />
        {/* Both outside `RedirectWhenSignedIn`, for the two reasons the `BARE`
            docblock sets out: `/reset` because the mail is opened as often as
            not on a browser that still has a session, and `/verify-email`
            because a signed-in user is the only kind it has. */}
        <Route path={RESET_PATH} element={<ResetPassword />} />
        <Route path={VERIFY_EMAIL_PATH} element={<VerifyEmail />} />
        <Route path={OFFLINE_PATH} element={<Offline />} />
        {/* Dev only. See PROBE_PATH — this is the line the production build
            removes, taking the component and its auth stub with it. */}
        {import.meta.env.DEV && <Route path={PROBE_PATH} element={<Probe />} />}
        {/* First run, and it belongs here for the same reason: it is FOR
            signed-in users, so `RedirectWhenSignedIn` would bounce it away the
            instant it rendered. Bare rather than chrome because M13-R28 asks
            for MFA setup as a full-screen step, and because a sidebar invites
            wandering off mid-flow. */}
        <Route path={WELCOME_PATH} element={<Welcome />} />
        {/* The Flinks popup's own landing page — see FLINKS_CALLBACK_PATH's
            own comment for why it sits here rather than behind RequireAuth. */}
        <Route path={FLINKS_CALLBACK_PATH} element={<FlinksCallback />} />
      </Route>

      {/* The gate, held back until now on purpose. Before a port could produce
          a session, putting this in front of the chrome would have made all 25
          placeholder screens and the /states design reference unreachable on
          dev — a guard nobody could get past, in exchange for nothing. #161 is
          the commit where that stops being true. */}
      <Route element={<RequireAuth welcomePath={WELCOME_PATH} />}>
      <Route element={<ChromeLayout />}>
        {/* The root is a real URL — the first one anyone types — and it had no
            route at all, so `/` rendered the 404. This is the one redirect the
            table should contain. */}
        <Route path={ROOT_PATH} element={<Navigate to="/overview" replace />} />

        {/**
         * The settings subtree renders inside `SettingsLayout` — #205.
         *
         * A layout route wrapping only the settings destinations, so the
         * sub-navigation column mounts once and survives navigation between
         * Profile, Security and the rest. Putting it around every route would
         * draw the column on Overview; putting it inside each screen would
         * remount it on every click and lose the scroll position with it.
         *
         * The children are still generated from the shared table, not typed
         * out — the same rule the block below follows, and the reason
         * `ALL_NAV_ITEMS` exists.
         */}
        {/* `/settings` is a section, not a screen — it redirects to the first
            item of its sub-navigation. This is what lets the sidebar foot link
            at `/settings` prefix-match the whole subtree, so it stays marked on
            every settings screen rather than only on Profile. */}
        <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />

        <Route element={<SettingsLayout />}>
          {SETTINGS_NAV.map((i) => (
            <Route
              key={i.path}
              path={`${i.path}/*`}
              element={SCREENS[i.path] ?? <Placeholder name={i.label} />}
            />
          ))}
        </Route>

        {/* Ranked above the generated `/categories/*` below regardless of
            where it sits in this file — see `CATEGORY_DETAIL_PATH`'s own
            comment for why a sibling Route is what settles the two. */}
        <Route path={CATEGORY_DETAIL_PATH} element={<CategoryDetail />} />
        {/* Ranked above the generated `/rules/*` — see `RULE_SUGGESTIONS_PATH`. */}
        <Route path={RULE_SUGGESTIONS_PATH} element={<Rules tab="suggestions" />} />
        {/* Below the literal above in rank, above the generated `/rules/*` — see `RULE_DETAIL_PATH`. */}
        <Route path={RULE_DETAIL_PATH} element={<RulePage />} />
        {/* Ranked above the generated `/accounts/*` below regardless of
            where it sits in this file — see `ACCOUNT_DETAIL_PATH`'s own
            comment for why a sibling Route is what settles the two. */}
        <Route path={ACCOUNT_DETAIL_PATH} element={<AccountDetail />} />
        {/* Ranked above the generated `/transactions/*` — see `TRANSACTION_DETAIL_PATH`. */}
        <Route path={TRANSACTION_DETAIL_PATH} element={<TransactionDetail />} />
        {/* Ranked above the SAME generated `/accounts/*`, above, for the
            reason `ACCOUNT_CONNECT_PATH`'s own comment gives — a literal
            second segment beats `:id` regardless of declaration order. */}
        <Route path={ACCOUNT_CONNECT_PATH} element={<Connect />} />
        {/* Plaid's OAuth return — #414. Not ranked against anything: `/plaid`
            is a standalone literal, not a segment competing with a
            generated `/*` subtree the way `ACCOUNT_CONNECT_PATH` is. */}
        <Route path={PLAID_OAUTH_RETURN_PATH} element={<PlaidOAuthReturn />} />
        {/* The reconnect match step. The path is `relinkRoutes.ts`'s one literal,
            so `relinkMatchPath` and this route cannot disagree. Ranked above the
            generated `/connections/*` by specificity, as `ACCOUNT_CONNECT_PATH` is. */}
        <Route path={RELINK_MATCH_ROUTE} element={<RelinkMatch />} />
        {/* Ranked above the generated `/merchants/*` below regardless of
            where it sits in this file — see `MERCHANT_TRANSACTIONS_PATH`'s
            own comment for why a sibling Route is what settles the two. */}
        <Route path={MERCHANT_TRANSACTIONS_PATH} element={<MerchantTransactions />} />
        {/* Ranked above the generated `/cashflow/*` — see `CASHFLOW_DETAIL_PATH`. */}
        <Route path={CASHFLOW_DETAIL_PATH} element={<CashflowDetail />} />
        {/* Ranked above the generated `/recurring/*` — see `RECURRING_CALENDAR_PATH`. */}
        <Route path={RECURRING_CALENDAR_PATH} element={<RecurringCalendarRedirect />} />
        {/* Ranked above the generated `/recurring/*` and below the calendar's literal — see `RECURRING_DETAIL_PATH`. */}
        {/* The literals outrank `:id`, so the list's links never fetch a bill
            called "new" (Ruling 41). Tasks 14–15 replace the two placeholders. */}
        <Route path={RECURRING_NEW_PATH} element={<RecurringNew />} />
        <Route path={RECURRING_ALL_PATH} element={<AllRecurring />} />
        <Route path={RECURRING_SUGGESTIONS_PATH} element={<Suggestions />} />
        <Route path={RECURRING_DETAIL_PATH} element={<RecurringDetail />} />
        <Route path={RECURRING_EDIT_PATH} element={<RecurringEdit />} />
        {/* Ranked above the generated `/budget/*` — see `TRANSACTION_DETAIL_PATH`. */}
        <Route path={BUDGET_DETAIL_PATH} element={<BudgetDetail />} />

        {/* Not `/settings`: the foot's Settings link is a SECTION, redirected
            above, and generating `/settings/*` for it made every unknown
            settings path (`/settings/integrations`, once removed) a "Settings
            is not built yet" placeholder instead of the Not found screen. */}
        {ALL_NAV_ITEMS.filter(
          (i) => i.path !== '/settings' && !SETTINGS_NAV.some((s) => s.path === i.path),
        ).map((i) => (
          <Route
            key={i.path}
            /**
             * `/*`, so a nav item owns its subtree.
             *
             * `ChromeLayout` matches the breadcrumb and the sidebar marker with
             * `matchPath({ end: false })` — a prefix match, deliberately, so
             * `/settings/profile` marks Settings. The route table matched
             * EXACTLY, so the two disagreed the other way: `/transactions/abc`
             * got a chrome saying "Transactions" over a body saying "Not found
             * is not built yet". Same matching on both halves, so they cannot.
             */
            path={`${i.path}/*`}
            element={SCREENS[i.path] ?? <Placeholder name={i.label} />}
          />
        ))}

        {/* Inside the chrome deliberately: a 404 with no navigation is a dead
            end, and CLAUDE.md asks an error to state the recovery path. */}
        <Route path="*" element={<NotFound />} />
      </Route>
      </Route>
    </Routes>
  )
}

export default AppRoutes
